// App.tsx
import React from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useParams, Navigate } from 'react-router-dom';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import GlobalBookPage from './pages/GlobalBookPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ServiceContactPage from './pages/ServiceContactPage';
import BookshelfPage from './pages/UserBookShelfPage';
import ShelfManagementPage from './pages/ShelfManagementPage';
import BibliographyManagementPage from './pages/BibliographyManagementPage';
import ImportBooksPage from './pages/ImportBooksPage';
import ExportBooksPage from './pages/ExportBooksPage';
import StickersPage from './pages/StickersPage';
import WithdrawnBooksPage from './pages/WithdrawnBooksPage';
import LogoutPage from './pages/LogoutPage';
import AboutStickerPage from './pages/AboutStickerPage';
import ManifestationDetailPage from './pages/ManifestationDetailPage';
import LegacyManifestationRedirect from './pages/LegacyManifestationRedirect';
import SearchResultPage from './pages/SearchResultPage';
import Header from './components/Header';
import GraphPageHeader from './components/GraphPageHeader';
import Footer from './components/Footer';
import Box from '@mui/material/Box';
import { AuthProvider } from './context/AuthContext';
import { RegistrationStatusProvider } from './context/RegistrationStatusContext';
import ProfileSettings from './pages/ProfileSettings';
import AboutReadLinkerPage from './pages/AboutReadLinkerPage';
import UserPage from './pages/UserPage';
import FriendRequestsPage from './pages/FriendRequestsPage';
import NotePage from './pages/NotePage';
import AuthorsDetailPage from './pages/AuthorsDetailPage';
import FollowingPage from './pages/FollowingPage';
import FollowersPage from './pages/FollowersPage';
import FriendsListPage from './pages/FriendsListPage';
import { FavoritesProvider } from './context/FavoritesContext';
import FavoriteBooksPage from './pages/FavoriteBooksPage';
import { HelmetProvider } from 'react-helmet-async';
import ListManagementPage from './pages/ListManagementPage';
import ListPage from './pages/ListPage';
import GraphPage from './pages/GraphPage';
import HighlightBooksPage from './pages/HighlightBooksPage';
import BookHighlightsPage from './pages/BookHighlightsPage';
import AdminRoute from './components/AdminRoute';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminAnnouncementPage from './pages/admin/AdminAnnouncementPage';
import AdminCachePage from './pages/admin/AdminCachePage';
import AdminHanmotoPage from './pages/admin/AdminHanmotoPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AnnouncementBanner from './components/AnnouncementBanner';

// 旧 /bookshelf/:userNameOnURL・/userlist/:userNameOnURL は、ユーザーページ(/:userNameOnURL)内の
// タブ（本棚・リスト）に統合されたため、既存リンク・ブックマークを壊さないようリダイレクトする
const BookshelfUserRedirect: React.FC = () => {
  const { userNameOnURL } = useParams<{ userNameOnURL: string }>();
  return <Navigate to={`/${userNameOnURL}?tab=bookshelf`} replace />;
};

const UserListRedirect: React.FC = () => {
  const { userNameOnURL } = useParams<{ userNameOnURL: string }>();
  return <Navigate to={`/${userNameOnURL}?tab=list`} replace />;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const isLoginPage = ['/login', '/register', '/forgot-password'].includes(location.pathname);
  const isGraphPage = location.pathname === '/';
  const isAdminPage = location.pathname.startsWith('/admin');
  // admin.readlinker.com は readlinker.com と同じビルドを配信しているだけなので、
  // このホスト名でアクセスされたときだけ "/" を管理画面ダッシュボードへ誘導する
  const isAdminHost = typeof window !== 'undefined' && window.location.hostname.startsWith('admin.');

  return (
    <>
      {!isAdminPage && <AnnouncementBanner />}
      {!isLoginPage && !isGraphPage && !isAdminPage && <Header />}
      {isGraphPage && <GraphPageHeader />}
      <Box
        // sx={
        //   isLoginPage
        //     ? {}
        //     : isGraphPage
        //     ? { mt: 7 }              // /graph だけ余白7
        //     : { mt: 10 }      // それ以外のページは従来通り
        // }
      >

      
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={isAdminHost ? <Navigate to="/admin" replace /> : <GraphPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/globalbook/:globalBookId" element={<GlobalBookPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/service-contact" element={<ServiceContactPage />} />
          <Route path="/bookshelf" element={<BookshelfPage />} />
          <Route path="/shelves/manage" element={<ShelfManagementPage />} />
          <Route path="/bibliography" element={<BibliographyManagementPage />} />
          <Route path="/importbooks" element={<ImportBooksPage />} />
          <Route path="/exportbooks" element={<ExportBooksPage />} />
          <Route path="/stickers" element={<StickersPage />} />
          <Route path="/withdrawn-books" element={<WithdrawnBooksPage />} />
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="/about-sticker" element={<AboutStickerPage />} />
          <Route path="/book/:rlBookId" element={<ManifestationDetailPage />} />
          <Route path="/manifestation/:KANRI_ID" element={<LegacyManifestationRedirect />} />
          <Route path="/search/:query" element={<SearchResultPage />} />
          <Route path="/profile" element={<ProfileSettings />} />
          <Route path="/about-readlinker" element={<AboutReadLinkerPage />} />
          <Route path="/friend-requests" element={<FriendRequestsPage />} />
          <Route path="/note/:noteID" element = {<NotePage />} />
          <Route path="/author/:tenkyo_id" element={<AuthorsDetailPage />} />
          <Route path="/bookshelf/:userNameOnURL" element={<BookshelfUserRedirect />} />
          <Route path="/favBooks" element={<FavoriteBooksPage />} />
          <Route path="/highlights" element={<HighlightBooksPage />} />
          <Route path="/highlights/:rlBookId" element={<BookHighlightsPage />} />
          <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
          <Route path="/admin/announcement" element={<AdminRoute><AdminAnnouncementPage /></AdminRoute>} />
          <Route path="/admin/cache" element={<AdminRoute><AdminCachePage /></AdminRoute>} />
          <Route path="/admin/hanmoto" element={<AdminRoute><AdminHanmotoPage /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          <Route path="/mylist" element={<ListManagementPage />} />
          <Route path="list/:bookListID" element={<ListPage />} />
          <Route path="/userlist/:userNameOnURL" element={<UserListRedirect />} />
          <Route path="/:username/following" element={<FollowingPage />} />
          <Route path="/:username/follower" element={<FollowersPage />} />
          <Route path="/:username/friendsList" element={<FriendsListPage />} />
          <Route path="/:userNameOnURL" element={<UserPage />} />

        </Routes>
      </Box>
      {/* {!isLoginPage && !isGraphPage && <Footer />} */}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
       <RegistrationStatusProvider>
       <FavoritesProvider>
       <HelmetProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<AppContent />} />
          </Routes>
        </Router>
        </HelmetProvider>
      </FavoritesProvider>
       </RegistrationStatusProvider>
    </AuthProvider>
  );
}

export default App;